import { redirect } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { isProblemError, rolesOf } from '@volee/api-client'

import { familyEnrolmentStatusQueryOptions } from '../data/family-enrolment.ts'
import { registrationStatusQueryOptions } from '../data/registration.ts'
import { NAV_PATHS } from '../navigation/paths.ts'
import { lastMeError, sessionOrNone } from './me.ts'
import { profileFor } from './profile.ts'

// What the entrance does with somebody who is already inside, for the door and the sign-in alike.

/** Whether the caller signed in and has no `app_user` yet — a pending applicant, whose
 *  session is real. `loadMe` flattens that 403 to `null`: the entrance reads the reason with
 *  `lastMeError`, after `sessionOrNone` has loaded it, rather than asking `/v1/me` again. */
// A pending session (no `app_user`) is a dirigente OR a family: route by which request exists,
// family first — sending both to the registration page looped a family 404 → `/` for ever.
async function pendingApplicantDestination(queryClient: QueryClient): Promise<string | null> {
  const error = lastMeError(queryClient)
  if (!isProblemError(error) || error.code !== 'auth.account_not_linked') return null
  if (await resolves(queryClient.ensureQueryData(familyEnrolmentStatusQueryOptions))) {
    return NAV_PATHS.familyEnrolmentStatus
  }
  if (await resolves(queryClient.ensureQueryData(registrationStatusQueryOptions))) {
    return NAV_PATHS.registrationStatus
  }
  return null
}

async function resolves(promise: Promise<unknown>): Promise<boolean> {
  try {
    await promise
    return true
  } catch {
    return false
  }
}

/** Returns for a visitor, and throws the redirect that takes anybody else where they belong:
 *  their profile, their request's status page, `/forbidden`. In `beforeLoad`, like
 *  `guardRoute`, so the entrance never flashes at someone about to be sent away. */
export async function sendInsidersOn(queryClient: QueryClient): Promise<void> {
  const me = await sessionOrNone(queryClient)
  if (me === null) {
    // A pending applicant typed the right credentials: their session exists and their
    // account does not, so they go to their own status page, not to the form again.
    const destination = await pendingApplicantDestination(queryClient)
    if (destination !== null) throw redirect({ to: destination })
    return
  }
  // A session with no facet has nowhere to land, and leaving it here would loop: it would
  // read the entrance as a visitor's page while holding a session.
  throw redirect({ to: profileFor(rolesOf(me)) ?? NAV_PATHS.forbidden })
}
